// Attendance — Overview tab: today's summary tiles (GET attendance/today-stats;
// its checkedInUsers stay mapped but no UI consumes them now) and the
// punch-record table (POST attendance/attendance-list) with the combined
// Filter dropdown (date range / status / groups / company structure), plus
// the row-click detail drawer. Mounted only while the tab is active, so the
// Live tab lands without any of these requests firing. Still NO search or
// orderBy on the wire — no search box, nothing sortable, and the mock's
// Region › Branch sub-lines have no payload.

import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Card,
  DataTable,
  FilterDropdown,
  Icon,
  TableFooter,
  type FilterDropdownCategory,
} from '@/components/common';
import useStickyTotal from '@/hooks/useStickyTotal';
import {
  AttendanceDateFilter,
  AttendanceStatusFilter,
  useGetAttendanceListQuery,
  useGetAttendanceTodayStatsQuery,
} from '@/api/endpoints/attendanceApi';
import { useDownloadClockInMediaQuery } from '@/api/endpoints/mediaApi';
import { useGetGroupsSelectionQuery } from '@/api/endpoints/groupsApi';
import { useGetStructureSelectionQuery } from '@/api/endpoints/structureApi';
import type { OrgUnit } from '@/mocks/structure';
import i18n from '@/i18n/config';
import { useAttendanceColumns } from '../attendanceColumns';
import AttendanceDetailDrawer from '../AttendanceDetailDrawer';

const flattenUnits = (units: OrgUnit[]): OrgUnit[] =>
  units.flatMap((u) => [u, ...flattenUnits(u.children ?? [])]);

// The date-range presets, in mock order. 'all' is the Filter category's
// "no filter" option (selected while the set is empty); Today is the landing
// default.
const DATE_OPTIONS = [
  { id: AttendanceDateFilter.Today, labelKey: 'attendance_range_today' },
  { id: AttendanceDateFilter.Last7Days, labelKey: 'attendance_range_7d' },
  { id: AttendanceDateFilter.Last30Days, labelKey: 'attendance_range_30d' },
  { id: AttendanceDateFilter.All, labelKey: 'attendance_range_all' },
] as const;

// Toolbar Filter category keys (rail order) + the landing selection shape.
const FILTER_KEYS = ['dateFilter', 'status', 'groups', 'companyStructures'] as const;
const initialFilters = (): Record<string, Set<string>> => ({
  ...Object.fromEntries(FILTER_KEYS.map((k) => [k, new Set<string>()])),
  dateFilter: new Set<string>([AttendanceDateFilter.Today]),
});

const STATUS_META = [
  {
    id: AttendanceStatusFilter.CheckedIn,
    labelKey: 'attendance_status_working',
    dot: 'var(--success)',
  },
  {
    id: AttendanceStatusFilter.CheckedOut,
    labelKey: 'attendance_status_out',
    dot: 'var(--neutral-fg)',
  },
] as const;

const OverviewTab = () => {
  const { t } = useTranslation();

  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [filters, setFilters] = useState<Record<string, Set<string>>>(initialFilters);
  // The person whose detail drawer is open (from the clicked row). Kept after
  // close so the exit animation doesn't lose its content.
  const [drawerTarget, setDrawerTarget] = useState<{
    userId: string;
    fullName: string;
    recordId: string;
  } | null>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);

  // Multi-select categories send bare id arrays, omitted while empty.
  const ids = <T extends string>(s: Set<string>) => (s.size ? ([...s] as T[]) : undefined);
  // The single-select date category: an empty set is its "no filter" option,
  // which on this wire is the value 'all' — never an omission.
  const dateFilter =
    ([...filters.dateFilter][0] as AttendanceDateFilter | undefined) ?? AttendanceDateFilter.All;

  const { data, currentData, isLoading, isFetching, isError } = useGetAttendanceListQuery(
    {
      dateFilter,
      status: ids<AttendanceStatusFilter>(filters.status),
      groups: ids(filters.groups),
      companyStructures: ids(filters.companyStructures),
      page,
      limit,
    },
    { refetchOnMountOrArgChange: true },
  );
  const { data: stats, fulfilledTimeStamp } = useGetAttendanceTodayStatsQuery(undefined, {
    refetchOnMountOrArgChange: true,
  });

  // Filter option sources.
  const { data: groups = [] } = useGetGroupsSelectionQuery();
  const { data: structure = [] } = useGetStructureSelectionQuery();

  // One combined Filters trigger (the app-wide FilterDropdown idiom) — the
  // Date range / Status / Groups / Company-structure categories share its rail.
  const filterCategories: FilterDropdownCategory[] = useMemo(
    () => [
      {
        key: 'dateFilter',
        label: t('attendance_filter_date'),
        icon: 'calendar-range',
        type: 'single',
        defaultOptionId: AttendanceDateFilter.All,
        options: DATE_OPTIONS.map((o) => ({ id: o.id, label: t(o.labelKey) })),
      },
      {
        key: 'status',
        label: t('attendance_filter_status'),
        icon: 'circle-dot',
        options: STATUS_META.map((s) => ({ id: s.id, label: t(s.labelKey), dotColor: s.dot })),
      },
      {
        key: 'groups',
        label: t('attendance_filter_groups'),
        icon: 'users',
        searchable: true,
        searchPlaceholder: t('members_search_groups'),
        options: groups.map((g) => ({ id: g.id, label: g.name })),
      },
      {
        key: 'companyStructures',
        label: t('attendance_filter_structure'),
        icon: 'network',
        searchable: true,
        searchPlaceholder: t('people_bulk_node_search_ph'),
        options: flattenUnits(structure)
          .filter((u) => u.level !== 0)
          .map((u) => ({ id: u.id, label: u.name })),
      },
    ],
    [t, groups, structure],
  );

  const items = useMemo(() => data?.items ?? [], [data]);

  // One batched resolve per table page: every punch photo goes up in a single
  // call; the result maps imageName → downloadUrl for the cells.
  const mediaRefs = useMemo(
    () =>
      items.flatMap((r) =>
        [r.clockInPhoto, r.clockOutPhoto].filter((p): p is NonNullable<typeof p> => Boolean(p)),
      ),
    [items],
  );
  const { data: mediaUrls } = useDownloadClockInMediaQuery(mediaRefs, {
    skip: mediaRefs.length === 0,
  });

  const columns = useAttendanceColumns(mediaUrls ?? {});
  // The API reports the record count on page-1 responses only (absent on
  // page 2+) — record it there and keep serving it while paging.
  const total = useStickyTotal(currentData?.total, page);
  const from = total === 0 ? 0 : (page - 1) * limit + 1;
  const to = Math.min(page * limit, total);

  const todayLabel = new Intl.DateTimeFormat(i18n.language, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  }).format(new Date());
  // "Last update" = when the stats actually arrived — the honest live marker
  // until the wire carries its own timestamp.
  const lastUpdateLabel = fulfilledTimeStamp
    ? new Intl.DateTimeFormat(i18n.language, { hour: 'numeric', minute: '2-digit' }).format(
        new Date(fulfilledTimeStamp),
      )
    : '—';

  const tiles = [
    { key: 'total', value: stats?.totalEmployees, badge: null },
    { key: 'checked_in', value: stats?.totalCheckedIn, badge: 'up' as const },
    { key: 'checked_out', value: stats?.totalCheckedOut, badge: 'down' as const },
  ];

  return (
    <>
      <Card className='attendance-page__summary'>
        <div className='attendance-page__summary-lead'>
          <span className='attendance-page__summary-tile' aria-hidden='true'>
            <Icon name='megaphone' size={22} />
          </span>
          <div className='attendance-page__summary-text'>
            <span className='attendance-page__summary-day'>
              {t('attendance_today', { date: todayLabel })}
            </span>
            <span className='attendance-page__summary-live'>
              {t('attendance_live_line', { time: lastUpdateLabel })}
            </span>
          </div>
        </div>
        {tiles.map((tile) => (
          <div key={tile.key} className='attendance-page__stat'>
            <span className='attendance-page__stat-label'>{t(`attendance_stat_${tile.key}`)}</span>
            <span className='attendance-page__stat-value'>{tile.value ?? '—'}</span>
            {tile.badge && (
              <span
                className={`attendance-page__stat-badge attendance-page__stat-badge--${tile.badge}`}
                aria-hidden='true'
              >
                <Icon
                  name={tile.badge === 'up' ? 'arrow-up-right' : 'arrow-down-right'}
                  size={14}
                />
              </span>
            )}
          </div>
        ))}
      </Card>

      <Card padded={false} className='attendance-page__records'>
        <div className='attendance-page__records-bar'>
          <div className='attendance-page__toolbar'>
            {/* Portaled to escape the records Card's overflow clipping, and
                flipped above the trigger when a short page leaves no room
                below (a fixed panel can't be scrolled into view). */}
            <FilterDropdown
              portal
              flip
              label={t('action_filter')}
              categories={filterCategories}
              value={filters}
              onApply={(next) => {
                setFilters(next);
                setPage(1);
              }}
              emptyText={t('text_no_results_found')}
            />
          </div>
          <span className='attendance-page__count'>
            {t('attendance_records_count', { count: total })}
          </span>
        </div>

        <DataTable
          columns={columns}
          rows={items}
          rowKey={(r) => r.id}
          loading={isLoading || isFetching}
          error={isError}
          loadingText={t('text_loading')}
          errorText={t('error_generic')}
          minWidth={900}
          onRowClick={(r) => {
            setDrawerTarget({ userId: r.userId, fullName: r.fullName, recordId: r.id });
            setDrawerOpen(true);
          }}
          empty={
            <>
              <Icon name='calendar-check' size={26} />
              {t('attendance_empty')}
            </>
          }
        />

        <TableFooter
          page={page}
          limit={limit}
          total={total}
          limits={[10, 20, 50]}
          onPage={setPage}
          onLimit={(n) => {
            setLimit(n);
            setPage(1);
          }}
          perPageLabel={t('attendance_items_per_page')}
          rangeText={t('attendance_range', { from, to, total })}
        />
      </Card>

      {drawerTarget && (
        <AttendanceDetailDrawer
          open={drawerOpen}
          onClose={() => setDrawerOpen(false)}
          userId={drawerTarget.userId}
          fullName={drawerTarget.fullName}
          initialRecordId={drawerTarget.recordId}
        />
      )}
    </>
  );
};

export default OverviewTab;
